/**
 * SbnLinkCard — compact "SbN link" strip that lives at the top of the
 * Health Record tab.
 *
 * Answers three questions in one glance:
 *   1. Is this client already linked to a Strength by Numbers record?
 *   2. If not, is there an obvious match we can auto-suggest by email?
 *   3. Where do I go if I want to search manually? (→ the SbN tab)
 *
 * We deliberately keep this small — full session details, exports, and
 * side-by-side comparison stay on the dedicated SbN tab. This card is
 * just a bridge so the clinician doesn't need to swap tabs mid-review
 * to remember they haven't linked yet.
 *
 * Auto-suggest strategy (mirrors the SbN tab):
 *   - Search SbN by the client's exact email first (some SbN accounts
 *     index it), then by the local-part of the email (before ``@``).
 *   - Any exact email match wins immediately — one-click "Link this".
 *   - No name-only guessing here: name matching would require
 *     the operator to eyeball the suggestion, which is what the SbN
 *     tab is for.
 *
 * If SbN isn't configured, the card renders nothing (silent).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Sparkles, Link2, Link2Off, ExternalLink, ArrowRight, Info,
} from "lucide-react";
import { toast } from "sonner";

export default function SbnLinkCard({ email, clientName, onOpenSbnTab }) {
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    configured: null,
    link: null,
    suggestion: null,
    suggestionSource: null,
    busy: false,
  });

  const load = useCallback(async () => {
    if (!email) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const [st, lk] = await Promise.all([
        api.get("/sbn/status"),
        api.get(`/sbn/links/by-email/${encodeURIComponent(email)}`),
      ]);
      const configured = !!st.data?.configured;
      const link = lk.data?.link || null;
      setState((s) => ({ ...s, loading: false, configured, link,
                          suggestion: null, suggestionSource: null }));
      // Kick off auto-suggest only when SbN is configured AND unlinked.
      if (configured && !link) suggestByEmail();
    } catch (e) {
      setState((s) => ({ ...s, loading: false, configured: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  async function suggestByEmail() {
    const candidates = [];
    if (email) candidates.push({ q: email, why: "email match" });
    // Also try the local-part-with-punctuation-cleaned, which matches
    // how many SbN accounts store the name (e.g. "jane.doe@x" → "jane doe").
    const local = (email || "").split("@")[0].replace(/[._-]+/g, " ").trim();
    if (local.length >= 2) candidates.push({ q: local, why: "email match" });

    for (const c of candidates) {
      try {
        const r = await api.get("/sbn/clients", {
          params: { q: c.q, take: 15 },
        });
        const rows = r.data?.clients || [];
        const emailLower = (email || "").toLowerCase();
        const exact = rows.find((x) => (x.email || "").toLowerCase() === emailLower);
        if (exact) {
          setState((s) => ({ ...s, suggestion: exact,
                              suggestionSource: "email" }));
          return;
        }
      } catch {/* silent — the tab covers manual search */}
    }
  }

  useEffect(() => { load(); }, [load]);

  async function linkTo(sbnClient) {
    setState((s) => ({ ...s, busy: true }));
    try {
      const nm = [sbnClient.first_name, sbnClient.last_name].filter(Boolean).join(" ");
      // Search results carry the SbN id under `client_id` (see
      // /api/sbn/clients response), not `id`. Fall back so we don't
      // regress if a caller ever hands us the raw client object shape.
      const sbnId = sbnClient.client_id || sbnClient.id;
      if (!sbnId) {
        toast.error("Link failed", { description: "SbN client id missing from search result." });
        return;
      }
      await api.post("/sbn/links", {
        email,
        sbn_client_id: sbnId,
        sbn_client_name: nm || null,
      });
      toast.success(`Linked to SbN client ${nm || sbnId}`);
      await load();
    } catch (e) {
      toast.error("Link failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setState((s) => ({ ...s, busy: false }));
    }
  }

  async function unlink() {
    if (!window.confirm("Remove the SbN link for this client?")) return;
    setState((s) => ({ ...s, busy: true }));
    try {
      await api.delete(`/sbn/links/by-email/${encodeURIComponent(email)}`);
      toast.success("Unlinked");
      await load();
    } catch (e) {
      toast.error("Unlink failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setState((s) => ({ ...s, busy: false }));
    }
  }

  function openSbnTab() {
    // Prefer the parent-supplied callback (keeps state synchronised with
    // the ClientDetail Tabs). Falls back to a hard nav for standalone use.
    if (onOpenSbnTab) return onOpenSbnTab();
    navigate(window.location.pathname + "#sbn");
  }

  // Silent when SbN isn't configured — no card at all.
  if (state.loading || state.configured === false) return null;

  const { link, suggestion, busy } = state;

  if (link) {
    // Linked state — green pill + name + quick actions.
    return (
      <Card
        className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/40 shadow-none flex items-center gap-3 flex-wrap"
        data-testid="sbn-link-card-linked"
      >
        <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 gap-1 font-normal">
          <Link2 className="h-3 w-3" /> Linked to SbN
        </Badge>
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-medium text-emerald-900 truncate">
            {link.sbn_client_name || link.sbn_client_id}
          </span>
          {link.linked_at && (
            <span className="ml-2 text-2xs text-emerald-800/70">
              since {new Date(link.linked_at).toLocaleDateString()}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={openSbnTab}
          className="h-7 text-2xs border-emerald-300"
          data-testid="sbn-link-card-open-tab"
        >
          Open SbN tab <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={unlink}
          disabled={busy}
          className="h-7 text-2xs text-red-700 hover:bg-red-50"
          data-testid="sbn-link-card-unlink"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2Off className="h-3 w-3 mr-1" />}
          Unlink
        </Button>
      </Card>
    );
  }

  if (suggestion) {
    // Auto-suggested match — amber "Suggested" pill + one-click link.
    const nm = [suggestion.first_name, suggestion.last_name].filter(Boolean).join(" ") || "—";
    return (
      <Card
        className="p-3 rounded-lg border border-amber-300 bg-amber-50/60 shadow-none flex items-center gap-3 flex-wrap"
        data-testid="sbn-link-card-suggestion"
      >
        <Badge className="bg-amber-600 text-white hover:bg-amber-600 gap-1 font-normal">
          <Sparkles className="h-3 w-3" /> SbN match found
        </Badge>
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-medium text-amber-900">{nm}</span>
          {suggestion.email && (
            <span className="ml-2 text-2xs text-amber-800/80 truncate">
              {suggestion.email}
            </span>
          )}
          <div className="text-2xs text-amber-800/70">
            Matched by email — link so future SOAP notes auto-pull dynamometry results.
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => linkTo(suggestion)}
          disabled={busy}
          className="h-7 text-2xs bg-amber-600 hover:bg-amber-700"
          data-testid="sbn-link-card-link-suggested"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
          Link this client
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={openSbnTab}
          className="h-7 text-2xs"
          data-testid="sbn-link-card-open-tab-suggestion"
        >
          Different client? <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </Card>
    );
  }

  // Unlinked and no email suggestion — quiet nudge to the manual tab.
  return (
    <Card
      className="p-2.5 rounded-lg border border-zinc-200 bg-zinc-50/40 shadow-none flex items-center gap-3 flex-wrap"
      data-testid="sbn-link-card-empty"
    >
      <Info className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
      <div className="flex-1 min-w-0 text-[0.75rem] text-zinc-600">
        Not linked to a Strength by Numbers record yet — no email match found.
        {clientName && (
          <span className="text-zinc-500"> Search by name in the SbN tab to bind manually.</span>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={openSbnTab}
        className="h-7 text-2xs"
        data-testid="sbn-link-card-open-tab-empty"
      >
        Search SbN <ExternalLink className="h-3 w-3 ml-1" />
      </Button>
    </Card>
  );
}
